/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback } from 'react';
import { Modal, Typography, useToast } from '@crisellevaldez/speculo-ui';
import { LoadingDialog } from '../../common/dialogs/LoadingDialog';
import { Flow } from '../../../hooks/useFlowsData';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { AdvancedSettingsModal } from './AdvancedSettingsModal';
import { AddTeamMemberModal } from './AddTeamMemberModal';
import { useQueryClient } from '@tanstack/react-query';
import { useApolloClient } from '@apollo/client/react';
import { buildTransferLineOptions, formatTransferNumbers, stripPhone } from '../../../utils/flows/formatTransferNumbers';
import { useUpdateFlow } from '../../../hooks/flows/useUpdateFlow';
import {
  createFlowExecutions,
  pauseScheduledFlow,
  pauseActiveFlow,
  resumePausedFlows,
} from '../../../api/flows';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import { useUser } from '../../../hooks/useUser';
import { AdminLockOverlay } from '../client/AdminLockOverlay';
import { type TeamMemberInput } from '../../../schemas/teamMember.schema';
import moment from 'moment-timezone';
import {
  FlowActions,
  FlowBasicInfo,
  FlowScheduling,
  FlowTransferLines,
  FlowVoicemail,
} from './edit';
import { useReviveEngageWebhook } from '../../../hooks/flows/useReviveEngageWebhook';

interface EditFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  flow: Flow | null;
  /** Called after a successful schedule/pause/resume so the parent can refetch */
  onSuccess?: () => void | Promise<void>;
  /** Callback when test contact selection changes */
  onTestContactChange?: (contactId: string | null) => void;
  /** Callback when add test contact modal opens/closes */
  onAddTestContactModalChange?: (isOpen: boolean) => void;
}

// Define which fields are editable for each status
const getEditableFields = (status: string) => {
  switch (status) {
    case 'draft':
    case 'paused':
      return {
        name: true,
        description: true,
        scheduledFor: true,
        voicemailContent: true,
        transferLines: true,
        advancedSettings: true,
      };
    case 'scheduled':
      return {
        name: true,
        description: true,
        scheduledFor: true,
        voicemailContent: true,
        transferLines: true,
        advancedSettings: false, // Can only view
      };
    case 'active':
      return {
        name: true,
        description: false, // Not editable in active state
        scheduledFor: false, // Not editable in active state
        voicemailContent: true,
        transferLines: true,
        advancedSettings: false, // Can only view
      };
    case 'finished':
      return {
        name: false,
        description: false,
        scheduledFor: false,
        voicemailContent: false,
        transferLines: false,
        advancedSettings: false,
      };
    default:
      return {
        name: false,
        description: false,
        scheduledFor: false,
        voicemailContent: false,
        transferLines: false,
        advancedSettings: false,
      };
  }
};

// Define which buttons are visible for each status
const getVisibleButtons = (status: string) => {
  switch (status) {
    case 'draft':
      return {
        update: true,
        schedule: true,
        delete: true,
        testCall: true,
        pause: false,
        resume: false,
      };
    case 'scheduled':
      return {
        update: true,
        schedule: false,
        delete: false,
        testCall: true,
        pause: true,
        resume: false,
      };
    case 'active':
      return {
        update: true,
        schedule: false,
        delete: false,
        testCall: true,
        pause: true,
        resume: false,
      };
    case 'paused':
      return {
        update: true,
        schedule: false,
        delete: true,
        testCall: true,
        pause: false,
        resume: true,
      };
    case 'finished':
      return {
        update: false,
        schedule: false,
        delete: true,
        testCall: true,
        pause: false,
        resume: false,
      };
    default:
      return {
        update: false,
        schedule: false,
        delete: false,
        testCall: false,
        pause: false,
        resume: false,
      };
  }
};

export const EditFlowModal: React.FC<EditFlowModalProps> = ({
  isOpen,
  onClose,
  flow,
  onSuccess,
  onTestContactChange,
  onAddTestContactModalChange,
}) => {
  const { organization } = useOrganizationStore();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const apolloClient = useApolloClient();
  const { users, addTeamMember } = useOrganizationUsers();
  const { updateFlow: updateFlowMutation, isUpdating } = useUpdateFlow();
  const { sendWebhook: sendReviveEngageWebhook } = useReviveEngageWebhook();

  const { isAdmin } = useUser();

  // Get editable fields and visible buttons. Agents (non-admins) are fully
  // read-only: force every editable field off so the body setup controls
  // (FlowBasicInfo, FlowVoicemail, FlowScheduling, FlowTransferLines) render
  // disabled. The allowed test-call path lives in FlowActions and is gated
  // separately via canEdit, so it stays available.
  const baseEditableFields = getEditableFields(flow?.status || '');
  const editableFields = isAdmin
    ? baseEditableFields
    : (Object.fromEntries(
        Object.keys(baseEditableFields).map((key) => [key, false])
      ) as typeof baseEditableFields);
  const visibleButtons = getVisibleButtons(flow?.status || '');

  // State
  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false);
  const [name, setName] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [descriptionError, setDescriptionError] = useState<string>('');
  const [scheduledError, setScheduledError] = useState<string>('');
  const [selectedDescription, setSelectedDescription] = useState<string>('');
  const [transferLines, setTransferLines] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [voicemailContent, setVoicemailContent] = useState('');
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);

  // Advanced settings state
  const [schedulingValue, setSchedulingValue] = useState<number>(0);
  const [schedulingUnit, setSchedulingUnit] = useState<string>('days');
  const [allowedDays, setAllowedDays] = useState<string[]>([]);
  const [allowedStartTime, setAllowedStartTime] = useState<string | null>(null);
  const [allowedEndTime, setAllowedEndTime] = useState<string | null>(null);
  const [excludeHolidays, setExcludeHolidays] = useState(false);
  const [useRoundRobin, setUseRoundRobin] = useState(false);
  const [flowTimezone, setFlowTimezone] = useState<string>(organization?.timezone || 'UTC');
  const [flowCountry, setFlowCountry] = useState<string>('US');

  // Validation functions
  const validateName = useCallback((value: string) => {
    if (!value.trim()) {
      setNameError('Flow name is required');
      return false;
    }
    setNameError('');
    return true;
  }, []);

  const validateDescription = useCallback(
    (value: string) => {
      if (!editableFields.description) {
        setDescriptionError('');
        return true;
      }
      if (!value.trim()) {
        setDescriptionError('Description is required');
        return false;
      }
      setDescriptionError('');
      return true;
    },
    [editableFields.description]
  );

  const validateScheduled = useCallback(
    (date: Date | null, time: string | null, timezoneOverride?: string) => {
      if (!editableFields.scheduledFor) {
        setScheduledError('');
        return true;
      }

      if (!date || !time) {
        setScheduledError('Scheduled date and time are required');
        return false;
      }

      // Use timezone override if provided, otherwise use flowTimezone
      const tz = timezoneOverride ?? flowTimezone;

      // Use the selected flow timezone for validation
      const now = moment().tz(tz);

      // Create the scheduled date directly in the selected timezone
      const scheduledDateTime = moment.tz(
        {
          year: date.getFullYear(),
          month: date.getMonth(),
          date: date.getDate(),
          hour: parseInt(time.split(':')[0]),
          minute: parseInt(time.split(':')[1]),
        },
        tz
      );

      // Compare in selected timezone
      const isToday = now.isSame(scheduledDateTime, 'day');

      if (!isToday && scheduledDateTime.isBefore(now.startOf('day'))) {
        setScheduledError('Selected date must not be in the past');
        return false;
      }

      if (isToday && scheduledDateTime.isBefore(now)) {
        setScheduledError('Selected time must be in the future');
        return false;
      }

      setScheduledError('');
      return true;
    },
    [flowTimezone, editableFields.scheduledFor]
  );

  // Form validation
  const validateForm = (): boolean => {
    let isValid = true;
    isValid = validateName(name) && isValid;
    isValid = validateDescription(description) && isValid;
    // Only validate scheduled_for if it's editable in current status
    if (editableFields.scheduledFor) {
      isValid = validateScheduled(selectedDate, selectedTime) && isValid;
    }
    return isValid;
  };

  // Update handlers
  const handleUpdate = async ({
    status,
    suppressSuccessToast = false,
  }: {
    status?: Flow['status'];
    suppressSuccessToast?: boolean;
  } = {}) => {
    if (!flow?.id) {
      showToast({
        message: 'Flow ID is missing',
        variant: 'error',
      });
      return;
    }

    if (!validateForm()) return;

    try {
      const scheduled_for =
        selectedDate && selectedTime
          ? moment
              .tz(
                {
                  year: selectedDate.getFullYear(),
                  month: selectedDate.getMonth(),
                  date: selectedDate.getDate(),
                  hour: parseInt(selectedTime.split(':')[0]),
                  minute: parseInt(selectedTime.split(':')[1]),
                },
                flowTimezone
              )
              .format()
          : undefined;

      // Partial update — only send fields the form edits.
      await updateFlowMutation({
        id: flow.id,
        name,
        description,
        description_id: selectedDescription,
        flow_type: flow.type,
        status,
        timezone: flowTimezone,
        country_code: flowCountry,
        scheduled_for,
        scheduling_window: schedulingValue,
        schedule_over_unit: schedulingUnit,
        allowed_days: allowedDays,
        allowed_hours: {
          start_hour: allowedStartTime ? parseInt(allowedStartTime.split(':')[0]) : 9,
          end_hour: allowedEndTime ? parseInt(allowedEndTime.split(':')[0]) : 18,
        },
        exclude_holidays: excludeHolidays,
        useRoundRobin: useRoundRobin,
        voicemail: voicemailContent,
        transfer_numbers: formatTransferNumbers(
          buildTransferLineOptions(users, flow?.transfer_numbers).filter((u) =>
            transferLines.some((t) => stripPhone(t) === stripPhone(u.value))
          )
        ),
      });

      if (!suppressSuccessToast) {
        showToast({
          message: 'Flow updated successfully',
          variant: 'success',
        });
      }
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to update flow',
        variant: 'error',
      });
    }
  };

  // Schedule handler
  const handleSchedule = async () => {
    if (!flow?.id) return;
    if (!validateForm()) return;

    try {
      setIsScheduling(true);

      // Save any pending changes first by reusing handleUpdate
      await handleUpdate({
        status: 'scheduled',
        suppressSuccessToast: true,
      });

      // Then schedule the flow
      await createFlowExecutions(flow.id);
      await queryClient.invalidateQueries({ queryKey: ['flows'] });
      await apolloClient.refetchQueries({ include: ['GetFlows'] });

      // Trigger webhook for Revive and Engage flows (fire-and-forget)
      if (flow.type === 'Revive' || flow.type === 'Engage') {
        sendReviveEngageWebhook(flow, 'scheduled');
      }

      showToast({
        message: 'Flow scheduled successfully',
        variant: 'success',
      });
      onClose();
      await onSuccess?.();
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to schedule flow',
        variant: 'error',
      });
    } finally {
      setIsScheduling(false);
    }
  };

  // Resume handler
  const handleResume = async () => {
    if (!flow?.id) return;
    if (!validateForm()) return;

    try {
      setIsResuming(true);

      // Save any pending changes first by reusing handleUpdate
      await handleUpdate();

      // Then resume the flow
      await resumePausedFlows(flow.id);
      await queryClient.invalidateQueries({ queryKey: ['flows'] });
      await apolloClient.refetchQueries({ include: ['GetFlows'] });
      showToast({
        message: 'Flow resumed successfully',
        variant: 'success',
      });
      onClose();
      await onSuccess?.();
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to resume flow',
        variant: 'error',
      });
    } finally {
      setIsResuming(false);
    }
  };

  // Pause handler
  const handlePause = async () => {
    if (!flow?.id) return;

    try {
      setIsPausing(true);
      if (flow.status === 'scheduled') {
        await pauseScheduledFlow(flow.id);
      } else if (flow.status === 'active') {
        await pauseActiveFlow(flow.id);
      }
      await queryClient.invalidateQueries({ queryKey: ['flows'] });
      await apolloClient.refetchQueries({ include: ['GetFlows'] });

      // Trigger webhook for Revive and Engage flows (fire-and-forget)
      if (flow.type === 'Revive' || flow.type === 'Engage') {
        sendReviveEngageWebhook(flow, 'paused');
      }

      showToast({
        message: 'Flow paused successfully',
        variant: 'success',
      });
      onClose();
      await onSuccess?.();
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to pause flow',
        variant: 'error',
      });
    } finally {
      setIsPausing(false);
    }
  };

  // Reset form when modal closes
  React.useEffect(() => {
    const resetForm = () => {
      setName('');
      setNameError('');
      setDescription('');
      setDescriptionError('');
      setSelectedDescription('');
      setTransferLines([]);
      setSelectedDate(null);
      setSelectedTime(null);
      setVoicemailContent('');
      setSchedulingValue(0);
      setSchedulingUnit('days');
      setAllowedDays([]);
      setAllowedStartTime(null);
      setAllowedEndTime(null);
      setExcludeHolidays(false);
      setUseRoundRobin(false);
      setFlowTimezone(organization?.timezone || 'UTC');
      setFlowCountry('US');
      setScheduledError('');
      setIsScheduling(false);
      setIsTesting(false);
      setIsPausing(false);
      setIsResuming(false);
      setIsAddingTeamMember(false);
      setShowAdvancedSettings(false);
      setShowAddTeamMember(false);
    };

    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, organization?.timezone]);

  // Initialize form values when flow changes or modal opens
  React.useEffect(() => {
    if (!flow || !isOpen) return;

    // Basic info
    setName(flow.name || '');

    // Set description and description_id
    const flowDescription = flow.description || '';
    setDescription(flowDescription);
    setSelectedDescription(flow.description_id || '');
    validateDescription(flowDescription);

    // Transfer lines — normalize stored phone values to match option values (avoids format mismatch)
    if (flow.transfer_numbers) {
      const storedNumbers = Object.values(flow.transfer_numbers);
      const options = buildTransferLineOptions(users, flow.transfer_numbers);
      const normalized = storedNumbers.map((stored) => {
        const match = options.find((o) => stripPhone(o.value) === stripPhone(stored));
        return match ? match.value : stored;
      });
      setTransferLines(normalized);
    } else {
      setTransferLines([]);
    }

    // Scheduling
    try {
      if (flow.scheduled_for) {
        // Parse the date in the original timezone
        const date = moment(flow.scheduled_for);

        if (date.isValid()) {
          // Convert to organization timezone
          const orgDate = date.tz(organization?.timezone || 'UTC');

          // Create date at noon UTC to avoid timezone shifts
          const dateStr = orgDate.format('YYYY-MM-DD');
          const utcNoon = moment.utc(dateStr).hours(12);
          setSelectedDate(utcNoon.toDate());
          setSelectedTime(orgDate.format('HH:mm'));

          if (editableFields.scheduledFor) {
            // Pass the original date string and time to avoid timezone shifts
            const dateStr = orgDate.format('YYYY-MM-DD');
            const timeStr = orgDate.format('HH:mm');
            const dateInOrgTz = moment
              .tz(dateStr, 'YYYY-MM-DD', organization?.timezone || 'UTC')
              .toDate();
            validateScheduled(dateInOrgTz, timeStr);
          }
        } else {
          setSelectedDate(null);
          setSelectedTime(null);
        }
      } else {
        setSelectedDate(null);
        setSelectedTime(null);
      }
    } catch (error) {
      setSelectedDate(null);
      setSelectedTime(null);
    }

    // Advanced settings
    const allowedHours = flow.allowed_hours || { start_hour: 9, end_hour: 18 };
    setSchedulingValue(flow.scheduling_window || 0);
    setSchedulingUnit(flow.schedule_over_unit || 'days');
    setAllowedDays(Array.isArray(flow.allowed_days) ? flow.allowed_days : []);

    if (typeof allowedHours.start_hour === 'number' && typeof allowedHours.end_hour === 'number') {
      setAllowedStartTime(`${allowedHours.start_hour.toString().padStart(2, '0')}:00`);
      setAllowedEndTime(`${allowedHours.end_hour.toString().padStart(2, '0')}:00`);
    } else {
      setAllowedStartTime(null);
      setAllowedEndTime(null);
    }

    setExcludeHolidays(flow.exclude_holidays || false);
    setUseRoundRobin(flow.useRoundRobin === true);
    // Initialize voicemail from GQL data (Firestore fallback handled in FlowVoicemail)
    setVoicemailContent(flow.voicemailContent || '');
    // Set timezone from flow or fall back to org timezone
    setFlowTimezone(flow.timezone || organization?.timezone || 'UTC');
    // Set country from flow or default to US
    setFlowCountry(flow.country_code || 'US');
  }, [flow, organization?.timezone, editableFields.scheduledFor, isOpen]);

  React.useEffect(() => {
    if (!flow?.transfer_numbers || !isOpen || users.length === 0) return;
    const options = buildTransferLineOptions(users, flow.transfer_numbers);
    setTransferLines((prev) =>
      prev.map((stored) => {
        const match = options.find((o) => stripPhone(o.value) === stripPhone(stored));
        return match ? match.value : stored;
      })
    );
  }, [users, flow?.transfer_numbers, isOpen]);

  if (!flow) return null;

  return (
    <>
      <LoadingDialog
        isOpen={isUpdating || isScheduling || isTesting || isPausing || isResuming}
        message={
          isScheduling
            ? 'Flow is scheduling...'
            : isTesting
              ? 'Testing call...'
              : isPausing
                ? 'Pausing flow...'
                : isResuming
                  ? 'Resuming flow...'
                  : 'Updating flow...'
        }
      />
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <Modal.Header>
          <Typography variant="h3">Edit {flow.type} Flow</Typography>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-6">
            {!isAdmin && (
              <AdminLockOverlay locked subject="Flows" disableFields={false}>
                <></>
              </AdminLockOverlay>
            )}
            <FlowBasicInfo
              flow={flow}
              name={name}
              description={description}
              selectedDescription={selectedDescription}
              nameError={nameError}
              descriptionError={descriptionError}
              onNameChange={(value) => {
                setName(value);
                validateName(value);
              }}
              onDescriptionChange={(value) => {
                setDescription(value);
                validateDescription(value);
              }}
              onSelectedDescriptionChange={setSelectedDescription}
              disabled={{
                name: !editableFields.name,
                description: !editableFields.description,
              }}
            />

            <FlowVoicemail
              flow={flow}
              voicemailContent={voicemailContent}
              onVoicemailContentChange={setVoicemailContent}
              disabled={!editableFields.voicemailContent}
            />

            <FlowScheduling
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              scheduledError={scheduledError}
              timezone={flowTimezone}
              onDateChange={(date) => {
                setSelectedDate(date);
              }}
              onTimeChange={(time) => {
                setSelectedTime(time);
              }}
              onTimezoneChange={setFlowTimezone}
              onCountryChange={setFlowCountry}
              onAdvancedSettingsClick={() => setShowAdvancedSettings(true)}
              onValidateScheduled={validateScheduled}
              // Agents can still OPEN Advanced Settings to view; the date/time
              // and timezone controls below stay disabled via editableFields
              // (all-false for agents). The Advanced Settings modal itself is
              // rendered read-only for agents.
              disabled={false}
              editableFields={editableFields}
            />

            <FlowTransferLines
              flow={flow}
              transferLines={transferLines}
              onTransferLinesChange={setTransferLines}
              onAddTeamMemberClick={() => setShowAddTeamMember(true)}
              disabled={!editableFields.transferLines}
              useRoundRobin={useRoundRobin}
              onUseRoundRobinChange={setUseRoundRobin}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex flex-col w-full gap-2">
            {/* Agents can still test a flow; mutating actions are disabled
                via canEdit on FlowActions. */}
            <div className="flex justify-between items-center">
              <FlowActions
                canEdit={isAdmin}
                flow={flow}
                organizationId={organization?.id || ''}
                currentDescription={description}
                visibleButtons={visibleButtons}
                onClose={onClose}
                onUpdate={handleUpdate}
                onSchedule={handleSchedule}
                onPause={handlePause}
                onResume={handleResume}
                isUpdating={isUpdating}
                isScheduling={isScheduling}
                isPausing={isPausing}
                isResuming={isResuming}
                onTestContactChange={onTestContactChange}
                onAddTestContactModalChange={onAddTestContactModalChange}
                disabled={{
                  update:
                    !name.trim() ||
                    !description.trim() ||
                    (!editableFields.scheduledFor ? false : !selectedDate || !selectedTime) ||
                    scheduledError !== '',
                  schedule:
                    !name.trim() ||
                    !description.trim() ||
                    !selectedDate ||
                    !selectedTime ||
                    scheduledError !== '',
                  testCall: flow.status === 'finished',
                  resume:
                    !name.trim() ||
                    !description.trim() ||
                    (!editableFields.scheduledFor ? false : !selectedDate || !selectedTime) ||
                    scheduledError !== '',
                }}
              />
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <AddTeamMemberModal
        isOpen={showAddTeamMember}
        onClose={() => {
          setIsAddingTeamMember(false);
          setShowAddTeamMember(false);
        }}
        onSubmit={async (data: TeamMemberInput) => {
          setIsAddingTeamMember(true);
          try {
            if (!organization?.id) {
              throw new Error('No organization ID');
            }
            await addTeamMember(organization.id, data);
            await queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
            setShowAddTeamMember(false);
            showToast({ message: 'Team member invited successfully', variant: 'success' });
          } catch (error) {
            showToast({
              message: error instanceof Error ? error.message : 'Error adding team member',
              variant: 'error',
            });
          } finally {
            setIsAddingTeamMember(false);
          }
        }}
        isSubmitting={isAddingTeamMember}
      />

      <AdvancedSettingsModal
        isOpen={showAdvancedSettings}
        onClose={() => setShowAdvancedSettings(false)}
        readOnly={!editableFields.advancedSettings}
        schedulingValue={schedulingValue.toString()}
        schedulingUnit={schedulingUnit}
        allowedDays={allowedDays}
        allowedStartTime={allowedStartTime}
        allowedEndTime={allowedEndTime}
        excludeHolidays={excludeHolidays}
        country={flowCountry}
        onSchedulingValueChange={(value) => setSchedulingValue(parseInt(value) || 0)}
        onSchedulingUnitChange={setSchedulingUnit}
        onAllowedDaysChange={setAllowedDays}
        onAllowedStartTimeChange={setAllowedStartTime}
        onAllowedEndTimeChange={setAllowedEndTime}
        onExcludeHolidaysChange={setExcludeHolidays}
        onCountryChange={setFlowCountry}
      />
    </>
  );
};
